import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Trophy, Star, Trash2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import api from '../utils/api';
import { SPORTS_CONFIG, getPrimaryStatLabel } from '../config/sports';
import { format } from 'date-fns';

// Stat labels for trends
const STAT_LABELS = {
  points: 'Points',
  rebounds: 'Rebounds',
  assists: 'Assists',
  goals: 'Goals',
  shots: 'Shots',
  kills: 'Kills',
  digs: 'Digs',
  aces: 'Aces',
  touchdowns: 'TDs',
  tackles: 'Tackles',
  receptions: 'Receptions',
  hits: 'Hits',
  rbi: 'RBIs',
  runs: 'Runs',
};

// Mini line chart component
function MiniTrendChart({ data, color }) {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => d.value);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((d.value - min) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg className="w-full h-12" viewBox="0 0 100 100" preserveAspectRatio="none">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {data.map((d, i) => {
        const x = (i / (data.length - 1)) * 100;
        const y = 100 - ((d.value - min) / range) * 80 - 10;
        return (
          <circle key={i} cx={x} cy={y} r="3" fill={color} />
        );
      })}
    </svg>
  );
}

export default function AthleteHome() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [games, setGames] = useState([]);
  const [trends, setTrends] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTrends, setShowTrends] = useState(true);
  const [trendLimit, setTrendLimit] = useState(10);

  useEffect(() => {
    fetchAthlete();
    fetchGames();
  }, [athleteId]);

  useEffect(() => {
    if (selectedSport) {
      fetchTrends();
    }
  }, [selectedSport, trendLimit]);

  const fetchAthlete = async () => {
    try {
      const response = await api.get(`/athletes/${athleteId}`);
      setAthlete(response.data);
      if (response.data.sports?.length > 0) {
        setSelectedSport(response.data.sports[0]);
      }
    } catch (error) {
      toast.error('Failed to load athlete');
      navigate('/athletes');
    }
  };

  const fetchGames = async () => {
    try {
      const response = await api.get('/games', { params: { athlete_id: athleteId } });
      setGames(response.data);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await api.get(`/athletes/${athleteId}/trends`, {
        params: { sport: selectedSport, limit: trendLimit }
      });
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  };

  const handleDeleteAthlete = async () => {
    try {
      await api.delete(`/athletes/${athleteId}`);
      toast.success('Athlete deleted');
      navigate('/athletes');
    } catch (error) {
      toast.error('Failed to delete athlete');
    }
  };

  const filteredGames = selectedSport
    ? games.filter(g => g.sport === selectedSport)
    : games;

  const getStats = () => {
    if (filteredGames.length === 0) return { totalPoints: 0, avgPoints: 0, gamesPlayed: 0 };
    const totalPoints = filteredGames.reduce((sum, g) => sum + g.points, 0);
    return {
      totalPoints,
      avgPoints: Math.round(totalPoints / filteredGames.length * 10) / 10,
      gamesPlayed: filteredGames.length
    };
  };

  const stats = getStats();
  const sportTrends = trends?.trends?.[selectedSport] || {};

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-[#4ADE80]" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-[#FF6B6B]" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'up') return '#4ADE80';
    if (trend === 'down') return '#FF6B6B';
    return '#00E5E5';
  };

  if (isLoading || !athlete) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5E5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000] pb-24">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/athletes')}
          className="flex items-center gap-1 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
        <div className="flex gap-2">
          <button
            data-testid="settings-btn"
            onClick={() => navigate(`/athlete/${athleteId}/achievements`)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <Trophy className="w-5 h-5" />
          </button>
          <button
            data-testid="delete-athlete-btn"
            onClick={() => setShowDeleteModal(true)}
            className="p-2 text-gray-400 hover:text-red-500"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Athlete Info */}
      <div className="text-center px-4 mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">{athlete.name}</h1>
        <div className="flex justify-center gap-2">
          {athlete.sports?.map(sport => (
            <span key={sport} className="text-2xl">{SPORTS_CONFIG[sport]?.emoji}</span>
          ))}
        </div>
      </div>

      {/* Sport Filter */}
      {athlete.sports?.length > 1 && (
        <div className="px-4 mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {athlete.sports.map(sport => {
              const config = SPORTS_CONFIG[sport];
              return (
                <button
                  key={sport}
                  data-testid={`sport-filter-${sport}`}
                  onClick={() => setSelectedSport(sport)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    selectedSport === sport
                      ? 'bg-[#00E5E5] text-black'
                      : 'bg-[#1A1A1A] text-gray-400'
                  }`}
                >
                  <span>{config?.emoji}</span>
                  <span className="font-semibold">{config?.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="px-4 mb-6">
        <div className="bg-[#1A1A1A] rounded-2xl p-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-[#00E5E5]">{stats.gamesPlayed}</p>
              <p className="text-sm text-gray-400">Games</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#FF9500]">{stats.totalPoints}</p>
              <p className="text-sm text-gray-400">Total {getPrimaryStatLabel(selectedSport)}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#4ADE80]">{stats.avgPoints}</p>
              <p className="text-sm text-gray-400">Avg {getPrimaryStatLabel(selectedSport)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Trends */}
      {Object.keys(sportTrends).length > 0 && (
        <div className="px-4 mb-6">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00E5E5]" />
              Performance Trends
            </h2>
            <button
              onClick={() => setShowTrends(!showTrends)}
              className="text-xs text-[#00E5E5]"
            >
              {showTrends ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {showTrends && (
            <>
              {/* Trend Period Selector */}
              <div className="flex gap-2 mb-3">
                {[5, 10, 20].map(num => (
                  <button
                    key={num}
                    onClick={() => setTrendLimit(num)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      trendLimit === num ? 'bg-[#00E5E5] text-black' : 'bg-[#333] text-gray-400'
                    }`}
                  >
                    Last {num}
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                {Object.entries(sportTrends).map(([statKey, statData]) => (
                  <div key={statKey} className="bg-[#1A1A1A] rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-white font-semibold">{STAT_LABELS[statKey] || statKey}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {getTrendIcon(statData.trend)}
                          <span className="text-xs text-gray-400">
                            Avg: {statData.average} | High: {statData.high} | Low: {statData.low}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: getTrendColor(statData.trend) }}>
                          {statData.data[statData.data.length - 1]?.value || 0}
                        </p>
                        <p className="text-xs text-gray-500">Latest</p>
                      </div>
                    </div>
                    <MiniTrendChart data={statData.data} color={getTrendColor(statData.trend)} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Games List */}
      <div className="px-4">
        <h2 className="text-lg font-bold text-white mb-3">Game Log</h2>
        {filteredGames.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-400">No games recorded yet</p>
            <p className="text-sm text-gray-500 mt-1">Tap + to add a new game</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredGames.map((game) => (
              <button
                key={game.id}
                data-testid={`game-card-${game.id}`}
                onClick={() => navigate(`/athlete/${athleteId}/game/${game.id}`)}
                className="w-full bg-[#1A1A1A] rounded-xl p-4 flex items-center justify-between hover:bg-[#252525] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{SPORTS_CONFIG[game.sport]?.emoji}</span>
                  <div className="text-left">
                    <p className="text-white font-semibold">
                      {format(new Date(game.date_played), 'MMM d, yyyy')}
                    </p>
                    <p className="text-sm text-gray-400">
                      {game.points} {getPrimaryStatLabel(game.sport)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {game.is_starred && <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />}
                  <span className="text-2xl font-bold text-[#00E5E5]">{game.points}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add Game Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
        <button
          data-testid="new-game-btn"
          onClick={() => navigate(`/athlete/${athleteId}/new-game`)}
          className="w-full bg-[#00E5E5] text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#5FFBFB] transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Game
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-sm">
            <h2 className="text-xl font-bold text-white mb-2">Delete Athlete?</h2>
            <p className="text-gray-400 mb-6">
              This will permanently delete {athlete.name} and all their games. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={handleDeleteAthlete}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-xl"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
